<template>
  <div class="fire-view-container">
    <MainHeader title="FIRE Calculator">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <div class="ud-section">
        <div class="hero-strip coral">
          <div class="hero-icon-wrap coral">
            <font-awesome-icon :icon="['fas', 'fire']" />
          </div>
          <div class="hero-text">
            <h2>FIRE Calculator</h2>
            <p>Find your FIRE number plus Lean, Fat, and Coast variants.</p>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">About You</h3>
          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Current age</label>
              <input
                v-model.number="enteredAge"
                type="number"
                inputmode="numeric"
                class="input"
                placeholder="Enter your age"
              />
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Retire by age</label>
              <input
                v-model.number="enteredRetireAge"
                type="number"
                inputmode="numeric"
                class="input"
                placeholder="Enter your retirement age"
              />
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Your spending</h3>
          <div class="ud-field">
            <label class="ud-label">Annual expenses</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="annualExpenses"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
            <p class="field-hint">
              Include everything: housing, food, insurance, travel, healthcare.
            </p>
          </div>

          <div class="ud-field">
            <label class="ud-label">Monthly expenses</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="monthlyExpenses"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Your assets</h3>
          <div class="ud-field">
            <label class="ud-label">Current invested assets</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="currentAssets"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
            <p class="field-hint">
              Invested accounts only (401k, IRA, brokerage). Exclude home equity
              unless you plan to sell.
            </p>
          </div>

          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Monthly contribution</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="monthlyContribution"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Expected return</label>
              <div class="input-with-suffix">
                <input
                  v-model.number="expectedReturn"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="7"
                  step="0.1"
                />
                <span class="suffix">%</span>
              </div>
            </div>
          </div>

          <div class="ud-field">
            <label class="ud-label">Safe withdrawal rate</label>
            <div class="input-with-suffix">
              <input
                v-model.number="withdrawalRate"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="4"
                step="0.1"
              />
              <span class="suffix">%</span>
            </div>
            <p class="field-hint">
              4% is the classic Trinity Study rule. Conservative planners use
              3.5% or 3%.
            </p>
          </div>
        </div>

        <div v-if="hasResults" class="wrapper-card results-card coral">
          <h3 class="section-label">Your FIRE number</h3>
          <div class="big-number">
            {{ formatCurrency(fireNumber) }}
            <span class="big-number-sub"
              >at {{ withdrawalRate || 4 }}% withdrawal</span
            >
          </div>

          <div class="fire-variants">
            <div class="variant-row">
              <span class="variant-label">Lean FIRE</span>
              <span class="variant-value">{{ formatCurrency(leanFire) }}</span>
              <span class="variant-desc"
                >{{ withdrawalRate || 4 }}% of reduced expenses</span
              >
            </div>
            <div class="variant-row">
              <span class="variant-label">Regular FIRE</span>
              <span class="variant-value">{{
                formatCurrency(fireNumber)
              }}</span>
              <span class="variant-desc">Current lifestyle maintained</span>
            </div>
            <div class="variant-row">
              <span class="variant-label">Fat FIRE</span>
              <span class="variant-value">{{ formatCurrency(fatFire) }}</span>
              <span class="variant-desc">Luxury lifestyle (2x expenses)</span>
            </div>
          </div>
        </div>

        <div class="result-stats">
          <div class="stat">
            <span class="stat-label">Years to FIRE</span>
            <span class="stat-value">{{ yearsToFire }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">At age</span>
            <span class="stat-value">{{ fireAge }}</span>
          </div>
          <div v-if="hasCoastResults" class="stat">
            <span class="stat-label">Coast FIRE</span>
            <span class="stat-value">{{ formatCurrency(coastFireNumber) }}</span>
          </div>
          <div v-if="hasCoastResults" class="stat">
            <span class="stat-label">Coast at age</span>
            <span class="stat-value">{{ coastAge }}</span>
          </div>

          <div v-if="!canReachFire && hasCoastResults" class="afford-verdict bad">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            <span
              >At this contribution rate, you won't reach FIRE in 50 years.
              Increase savings or reduce target.</span
            >
          </div>

          <div v-if="alreadyFired && hasCoastResults" class="afford-verdict good">
            <font-awesome-icon :icon="['fas', 'circle-check']" />
            <span
              >You're already past your FIRE number. Your investments can
              support your expenses indefinitely.</span
            >
          </div>
        </div>

        <div
          v-if="hasResults && canReachFire && !alreadyFired"
          class="wrapper-card"
        >
          <h3 class="section-label">Year-by-year projection</h3>
          <div class="schedule-table">
            <div class="schedule-header">
              <span>Age</span>
              <span>Assets</span>
              <span>Contributions</span>
              <span>Progress</span>
            </div>
            <div
              v-for="row in projection"
              :key="row.age"
              class="schedule-row"
              :class="{
                'fire-row': row.reachedFire,
                'coast-row': row.reachedCoast,
              }"
            >
              <span>{{ row.age }}</span>
              <span>{{ formatCurrency(row.assets) }}</span>
              <span>{{ formatCurrency(row.totalContributed) }}</span>
              <span>
                <span v-if="row.reachedFire" class="fire-badge">FIRE</span>
                <span v-else-if="row.reachedCoast" class="coast-badge"
                  >Coast</span
                >
                <span v-else>{{ row.percentToFire }}%</span>
              </span>
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Scenario tester</h3>
          <p class="section-blurb">What if your expenses or returns change?</p>

          <div class="scenario-grid">
            <div
              v-for="s in scenarios"
              :key="s.name"
              class="scenario-card"
              :class="{ active: activeScenario === s.name }"
              @click="applyScenario(s)"
            >
              <span class="scenario-name">{{ s.name }}</span>
              <span class="scenario-desc">{{ s.desc }}</span>
            </div>
          </div>

          <div v-if="scenarioResult" class="scenario-result">
            <div class="delta-row">
              <span>FIRE number</span>
              <strong :class="scenarioResult.fireDelta < 0 ? 'good' : 'bad'">
                {{ scenarioResult.fireDelta < 0 ? '−' : '+'
                }}{{ formatCurrency(Math.abs(scenarioResult.fireDelta)) }}
              </strong>
            </div>
            <div class="delta-row">
              <span>Years to FIRE</span>
              <strong v-if="scenarioResult.yearsDelta === null" class="bad">
                Not reachable
              </strong>
              <strong v-else :class="scenarioResult.yearsDelta < 0 ? 'good' : 'bad'">
                {{ scenarioResult.yearsDelta < 0 ? '−' : '+'
                }}{{ Math.abs(scenarioResult.yearsDelta) }} yr
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../../store.js';
import MainHeader from '../../components/View-Header.vue';

export default {
  name: 'CalculatorFire',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    const store = useMyStore();

    const ageFromDob = (dob) => {
      if (!dob) return null;
      const d = new Date(dob);
      if (isNaN(d.getTime())) return null;
      const now = new Date();
      let age = now.getFullYear() - d.getFullYear();
      const m = now.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
      return age > 0 && age < 120 ? age : null;
    };

    const profileAge = computed(() => ageFromDob(store.user?.dateOfBirth));
    const enteredRetireAge = ref(null);
    const enteredAge = ref(profileAge.value ?? null);
    const annualExpenses = ref(null);
    const currentAssets = ref(null);
    const monthlyContribution = ref(null);
    const expectedReturn = ref(7);
    const withdrawalRate = ref(4);
    const activeScenario = ref(null);

    const monthlyExpenses = computed({
      get: () =>
        annualExpenses.value === null || annualExpenses.value === ''
          ? null
          : Math.round(annualExpenses.value / 12),
      set: (val) => {
        annualExpenses.value = val === null || val === '' ? null : val * 12;
      },
    });

    const hasResults = computed(
      () =>
        annualExpenses.value > 0 &&
        withdrawalRate.value > 0 &&
        enteredAge.value > 0
    );

    const hasCoastResults = computed(
      () => hasResults.value && enteredRetireAge.value > 0
    );

    const fireNumber = computed(() =>
      Math.round(annualExpenses.value / (withdrawalRate.value / 100))
    );

    const leanFire = computed(() => Math.round(fireNumber.value * 0.7));
    const fatFire = computed(() => Math.round(fireNumber.value * 2));

    const returnRate = computed(() => (expectedReturn.value || 0) / 100);

    const yearsToRetire = computed(() =>
      Math.max(0, enteredRetireAge.value - enteredAge.value)
    );

    const coastFireNumber = computed(() =>
      Math.round(fireNumber.value / Math.pow(1 + returnRate.value, yearsToRetire.value))
    );

    const projection = computed(() => {
      if (!hasResults.value) return [];

      const target = fireNumber.value;
      const contribution = monthlyContribution.value || 0;
      const annualReturn = returnRate.value;
      const monthlyReturn = Math.pow(1 + annualReturn, 1 / 12) - 1;

      const startAge = enteredAge.value;
      const retirementAge = enteredRetireAge.value;

      let balance = currentAssets.value || 0;
      let totalContributed = 0;

      const rows = [];

      for (let month = 0; month <= 50 * 12; month++) {
        const exactAge = startAge + month / 12;

        if (month % 12 === 0) {
          const displayedAge = startAge + month / 12;
          const remainingYears = Math.max(
            0,
            retirementAge - displayedAge
          );

          const coastTargetAtAge =
            target / Math.pow(1 + annualReturn, remainingYears);

          rows.push({
            age: Math.round(displayedAge),
            assets: Math.round(balance),
            totalContributed: Math.round(totalContributed),
            percentToFire: Math.min(
              100,
              Math.round((balance / target) * 100)
            ),
            coastFireNumber: Math.round(coastTargetAtAge),
            reachedFire: balance >= target,
            reachedCoast: balance >= coastTargetAtAge,
          });

          if (balance >= target) break;
        }

        balance =
          balance * (1 + monthlyReturn) +
          contribution;

        totalContributed += contribution;
      }

      return rows;
    });

    const yearsToFire = computed(() => {
      const rows = projection.value;
      const fireRow = rows.find((r) => r.reachedFire);
      return fireRow ? fireRow.age - enteredAge.value : '50+';
    });

    const monthsToFire = computed(() => {
      const target = fireNumber.value;
      const monthlyContributionAmount = monthlyContribution.value || 0;
      const monthlyRate =
        Math.pow(1 + returnRate.value, 1 / 12) - 1;

      let balance = currentAssets.value || 0;

      if (balance >= target) return 0;

      for (let month = 1; month <= 50 * 12; month++) {
        balance =
          balance * (1 + monthlyRate) +
          monthlyContributionAmount;

        if (balance >= target) return month;
      }

      return Infinity;
    });

    const fireAge = computed(() => {
      if (monthsToFire.value === Infinity) return '50+';

      return Number(
        (
          enteredAge.value +
          monthsToFire.value / 12
        ).toFixed(2)
      );
    });
    const coastAge = computed(() => {
      const rows = projection.value;
      const coastRow = rows.find((r) => r.reachedCoast);
      return coastRow ? coastRow.age : '—';
    });

    const canReachFire = computed(() => {
      const rows = projection.value;
      return rows.some((r) => r.reachedFire);
    });

    const alreadyFired = computed(
      () => currentAssets.value >= fireNumber.value
    );

    const scenarios = [
      {
        name: 'Market crash',
        desc: '−20% assets now',
        apply: (i) => ({ ...i, currentAssets: (i.currentAssets || 0) * 0.8 }),
      },
      {
        name: 'High inflation',
        desc: '+25% expenses',
        apply: (i) => ({
          ...i,
          annualExpenses: (i.annualExpenses || 0) * 1.25,
        }),
      },
      {
        name: 'Boost savings',
        desc: '+$500/mo',
        apply: (i) => ({
          ...i,
          monthlyContribution: (i.monthlyContribution || 0) + 500,
        }),
      },
      {
        name: 'Conservative',
        desc: '3.5% SWR',
        apply: (i) => ({ ...i, withdrawalRate: 3.5 }),
      },
      {
        name: 'Aggressive',
        desc: '10% returns',
        apply: (i) => ({ ...i, expectedReturn: 10 }),
      },
    ];

    const applyScenario = (scenario) => {
      activeScenario.value =
        activeScenario.value === scenario.name ? null : scenario.name;
    };

    const baseValues = computed(() => ({
      annualExpenses: annualExpenses.value,
      currentAssets: currentAssets.value || 0,
      monthlyContribution: monthlyContribution.value || 0,
      expectedReturn: expectedReturn.value || 0,
      withdrawalRate: withdrawalRate.value,
    }));

    const fireOf = (v) => Math.round(v.annualExpenses / (v.withdrawalRate / 100));

    const scenarioResult = computed(() => {
      if (!activeScenario.value || !hasResults.value) return null;
      const s = scenarios.find((x) => x.name === activeScenario.value);
      if (!s) return null;
      
      const base = baseValues.value;
      const alt = s.apply(base);
      
      const baseYears = computeYearsToFire(base, fireOf(base));
      const altYears = computeYearsToFire(alt, fireOf(alt));

      const yearsDelta = (baseYears === Infinity || altYears === Infinity) 
        ? null 
        : altYears - baseYears;

      return {
        fireDelta: fireOf(alt) - fireOf(base),
        yearsDelta: yearsDelta,
      };
    });

    function computeYearsToFire(i, target) {
      let balance = i.currentAssets;
      const annualContribution = i.monthlyContribution * 12;
      const r = i.expectedReturn / 100;

      if (balance >= target) return 0;
      if (annualContribution <= 0 && r <= 0) return Infinity;

      for (let year = 1; year <= 50; year++) {
        balance = balance * (1 + r) + annualContribution;
        if (balance >= target) return year;
      }

      return Infinity;
    }

    function formatCurrency(n) {
      if (!isFinite(n) || n === null || n === undefined) return '$0';
      return '$' + Math.round(n).toLocaleString('en-US');
    }

    return {
      router,
      monthlyExpenses,
      hasResults,
      hasCoastResults,
      fireNumber,
      leanFire,
      fatFire,
      coastFireNumber,
      projection,
      yearsToFire,
      fireAge,
      coastAge,
      canReachFire,
      alreadyFired,
      scenarios,
      activeScenario,
      applyScenario,
      scenarioResult,
      formatCurrency,
      enteredRetireAge,
      enteredAge,
      annualExpenses,
      currentAssets,
      monthlyContribution,
      expectedReturn,
      withdrawalRate
    };
  },
};
</script>

<style scoped>
.fire-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.pointer {
  cursor: pointer;
}

.hero-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #fdeee7 0%, #fff8f5 100%);
  border-radius: var(--radius-lg);
  margin-bottom: 16px;
}
.hero-strip.coral {
  background: linear-gradient(135deg, #fdeee7 0%, var(--purple-50) 100%);
}
.hero-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-accent-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hero-icon-wrap.coral {
  background: #d85a30;
}
.hero-icon-wrap :deep(svg) {
  color: white;
  font-size: 24px;
}
.hero-text h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.ud-section {
  padding: 16px;
  padding-bottom: 80px;
}

.wrapper-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
}
.dark-theme .wrapper-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

.section-label {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}
.section-blurb {
  font-size: 13px;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.light-theme .section-blurb {
  color: #666;
}

.ud-field {
  margin-bottom: 14px;
}
.ud-field:last-child {
  margin-bottom: 0;
}
.ud-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.input-with-prefix,
.input-with-suffix {
  position: relative;
  display: flex;
  align-items: center;
}
.input-with-prefix .input {
  padding-left: 28px;
}
.input-with-suffix .input {
  padding-right: 32px;
}
.prefix,
.suffix {
  position: absolute;
  color: #999;
  font-size: 14px;
  pointer-events: none;
}
.prefix {
  left: 12px;
}
.suffix {
  right: 12px;
}

.input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e5e5e5;
  border-radius: var(--radius-md);
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  color: var(--color-primary, #213547);
  background: white;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.input:focus {
  border-color: var(--color-accent-purple);
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.dark-theme .input {
  background: #1a2535;
  border-color: #2a3a4e;
  color: white;
}

.dual-input {
  display: flex;
  gap: 10px;
}
.flex-1 {
  flex: 1;
}

.field-hint {
  font-size: 12px;
  margin: 6px 0 0 0;
  line-height: 1.4;
}

.light-theme .field-hint {
  color: #888;
}

.results-card {
  background: linear-gradient(135deg, #fdeee7 0%, var(--purple-50) 100%);
  border: 1px solid #f5cdb8;
}
.results-card.coral {
  background: linear-gradient(135deg, #fdeee7 0%, #fff8f5 100%);
}
.dark-theme .results-card {
  background: linear-gradient(135deg, #2e1d18 0%, #1e1a40 100%);
  border-color: #4a2a20;
}

.big-number {
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
}
.big-number-sub {
  font-size: 13px;
  font-weight: 400;
  color: #888;
}

.fire-variants {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.variant-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: white;
  border-radius: var(--radius-md);
}
.dark-theme .variant-row {
  background: #212e3e;
}
.variant-label {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  min-width: 80px;
}
.variant-value {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-accent-purple);
  min-width: 100px;
}
.variant-desc {
  font-size: 12px;
  color: #888;
  flex: 1;
  text-align: right;
}

.result-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}
.stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background: var(--teal-50, #e5f7f1);
  border-radius: var(--radius-sm);
}
.stat-label {
  font-size: 11px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.stat-value {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}

.afford-verdict {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius-md);
  font-size: 14px;
  background: white;
  line-height: 1.4;
}
.afford-verdict :deep(svg) {
  font-size: 20px;
  flex-shrink: 0;
}
.afford-verdict.good {
  color: var(--color-accent-green, #5dcaa5);
  border-left: 3px solid var(--color-accent-green);
}
.afford-verdict.bad {
  color: #d45a4a;
  border-left: 3px solid #d45a4a;
}

.schedule-table {
  margin-top: 12px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: var(--radius-sm);
}
.schedule-header,
.schedule-row {
  display: grid;
  grid-template-columns: 50px 1fr 1fr 80px;
  font-size: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #f5f5f5;
}
.schedule-header {
  background: var(--purple-50);
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  position: sticky;
  top: 0;
}
.schedule-row.fire-row {
  background: var(--teal-50, #e5f7f1);
  font-weight: 500;
}
.schedule-row.coast-row {
  background: #fff8e6;
  font-weight: 500;
}
.dark-theme .schedule-row.coast-row {
  background: #2e2718;
}

.fire-badge {
  background: var(--color-accent-green, #5dcaa5);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
}
.coast-badge {
  background: #e2a53c;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
}

.scenario-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
}
.scenario-card {
  padding: 12px;
  background: #f8f8f8;
  border-radius: var(--radius-md);
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}
.scenario-card:hover {
  background: var(--purple-50);
}
.scenario-card.active {
  border-color: var(--color-accent-purple);
  background: var(--purple-50);
}
.dark-theme .scenario-card {
  background: #1a2535;
}
.dark-theme .scenario-card.active {
  background: #1e1a40;
}
.scenario-name {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}
.light-theme .scenario-name {
  color: var(--color-accent-teal, #004751);
}
.dark-theme .scenario-name {
  color: var(--color-teal-mint, #004751);
}
.scenario-desc {
  display: block;
  font-size: 11px;
}

.light-theme .scenario-desc {
  color: #888;
}

.scenario-result {
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: var(--radius-md);
  padding: 12px;
}
.dark-theme .scenario-result {
  background: #212e3e;
  border-color: #2a3a4e;
}
.delta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
}
.delta-row span {
  color: #555;
}
.delta-row strong {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
}
.delta-row strong.good {
  color: var(--color-accent-green, #5dcaa5);
}
.delta-row strong.bad {
  color: #d45a4a;
}
</style>
